import axios from "axios";
import { instalarPulso } from "@/pulso/pulso";
import { anotarSesionCerrada } from "@/lib/telemetriaChofer";
import { esCambioDeClaveRequerido, EVENTO_CLAVE_REQUERIDA } from "@/lib/claveRequerida";
import { avisoDeSesionReemplazada, CABECERA_EQUIPO, guardarAvisoIngreso, idEquipo } from "@/lib/equipo";

export const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  // **Los arrays van con la clave repetida, sin corchetes.** Axios serializa por default
  // `falta[]=a&falta[]=b`, y FastAPI lee `falta=a&falta=b`: los filtros de selección
  // múltiple llegaban vacíos y la pantalla se veía como si no filtrara nada. El chip se
  // encendía, la lista no cambiaba, y no había ningún error a la vista.
  paramsSerializer: {
    serialize: (params) => {
      const p = new URLSearchParams();
      Object.entries(params || {}).forEach(([k, v]) => {
        if (v === undefined || v === null || v === "") return;
        if (Array.isArray(v)) v.forEach((x) => (x !== undefined && x !== null) && p.append(k, x));
        else p.append(k, v);
      });
      return p.toString();
    },
  },
});

// ── Global loading counter ─────────────────────────────────────
// Exposed via the `qb:loading` window event for GlobalLoadingBar.
// Excluye polling de /unidades/stats si quisiéramos mute, pero por
// ahora contamos TODO request HTTP para feedback honesto.
let _activeRequests = 0;
const _emitLoading = () => {
  try {
    window.dispatchEvent(
      new CustomEvent("qb:loading", { detail: { active: _activeRequests } })
    );
  } catch (_) { /* SSR-safe */ }
};

// 8-oct-2026 — la MARCA de la app: cada pedido dice que viene de SIL web y con qué build. El centinela la mira:
// un pedido de un rol de oficina sin esta marca no salió de la aplicación (ver CLAUDE.md, «clientes automáticos»).
// Siempre presente, desde el primer pedido del arranque (a diferencia de la pantalla de Pulso, que recién se sabe
// cuando la página carga).
export const MARCA_CLIENTE = `web/${process.env.REACT_APP_BUILD_INFO || "dev"}`;

// Attach JWT + count up
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("qb_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (config.headers) {
    config.headers["X-SIL-Cliente"] = MARCA_CLIENTE;
    // 15-oct-2026 — de qué equipo sale el pedido (`lib/equipo.js`): Seguridad cuenta desde cuántos se usa cada cuenta.
    config.headers[CABECERA_EQUIPO] = idEquipo();
  }
  if (!config.__silent) {
    _activeRequests += 1;
    _emitLoading();
  }
  return config;
});

// Count down + logout on 401
export const EVENTO_PERMISO_RECHAZADO = "qb:permiso-rechazado";

api.interceptors.response.use(
  (r) => {
    if (!r.config?.__silent) {
      _activeRequests = Math.max(0, _activeRequests - 1);
      _emitLoading();
    }
    return r;
  },
  (err) => {
    if (!err?.config?.__silent) {
      _activeRequests = Math.max(0, _activeRequests - 1);
      _emitLoading();
    }
    if (err?.response?.status === 401) {
      anotarSesionCerrada("401");            // solo anota si era la sesión de un chofer (diagnóstico)
      // 15-oct-2026 — otro equipo entró con este usuario (una sesión por cuenta): el ingreso lo va a decir.
      guardarAvisoIngreso(avisoDeSesionReemplazada(err));
      localStorage.removeItem("qb_token");
      localStorage.removeItem("qb_user");
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    // 9-oct-2026 — la clave venció (o es temporal) con la sesión abierta: la sesión VALE, hay que cambiar la clave.
    // Se avisa una vez y `AuthContext` vuelve a preguntar quién es: la pantalla de cambio aparece sola.
    if (esCambioDeClaveRequerido(err)) {
      try { window.dispatchEvent(new CustomEvent(EVENTO_CLAVE_REQUERIDA)); } catch (_) { /* SSR-safe */ }
    } else if (err?.response?.status === 403 && !err?.config?.__silent) {
      // 10-oct-2026 — rechazo por permiso: los permisos que la pantalla tiene cargados pueden estar viejos (el
      // administrador cambió el rol con la sesión abierta). `PermisosContext` y el menú vuelven a preguntar.
      // Solo ante un pedido de la persona o de la carga de una pantalla: un sondeo en segundo plano (`__silent`)
      // que se rechaza cada minuto no puede poner a la app a recargar sus permisos sin parar.
      try { window.dispatchEvent(new CustomEvent(EVENTO_PERMISO_RECHAZADO)); } catch (_) { /* SSR-safe */ }
    }
    return Promise.reject(err);
  }
);

// Pulso (7-oct-2026): le dice al backend qué pantalla está abierta, con una cabecera en las llamadas que ya existen.
// No agrega pedidos (salvo un latido cuando la pestaña está a la vista y quieta) y nunca rompe uno: ver `pulso/pulso.js`.
instalarPulso(api);

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Ocurrió un error";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" · ");
  // Contrato unificado de validación (ítem 13): { nivel, codigo, mensaje }.
  if (d && typeof d === "object" && d.mensaje) return d.mensaje;
  return String(d);
}
